<div class="dashboard-body-action ai-usage-action">
  <div class="fl-l">
    <app-date-range-calendar
      [multiple]="true"
      [dateRange]="initialDateRange"
      [placeholder]="'GENERALS.SELECT-DATE' | translate"
      (change)="setDateRange($event)"
    ></app-date-range-calendar>
  </div>

  <!--
    Inline beside the date range, not behind the shared <app-filters> popover.
    That component hides its dropdowns behind an "Advanced search" toggle and
    dismisses them on any outside click — right for a long optional filter
    list, wrong for three controls that are the point of this tab.

    All three always render, including any with no options: the User dropdown
    is empty until calls carry an author, and hiding it there would read as the
    feature missing rather than as that account having nothing attributed yet.
  -->
  <mat-form-field
    appearance="outline"
    class="ai-usage-filter"
    *ngFor="let filter of availableFilters"
  >
    <mat-label>{{ filter.label }}</mat-label>
    <!--
      panelWidth null lets the dropdown size to its content instead of to the
      trigger. Model identifiers run to ~45 characters; pinned to the trigger
      width they would all be clipped to the same unreadable prefix.
    -->
    <mat-select
      multiple
      [panelWidth]="null"
      [placeholder]="'GENERALS.SELECT' | translate"
      [ngModel]="filterValue(filter.id)"
      (ngModelChange)="setFilter(filter.id, $event)"
    >
      <mat-option *ngFor="let option of filter.data" [value]="option.id">
        {{ option.text }}
      </mat-option>
      <!--
        A filter with nothing to offer still renders, so it needs to say why
        rather than opening onto an empty panel. Disabled so it cannot be
        selected into the model.
      -->
      <mat-option *ngIf="!filter.data.length" disabled>
        {{ "GENERALS.NO-ITEMS-FOUND" | translate }}
      </mat-option>
    </mat-select>
  </mat-form-field>

  <div
    *ngIf="hasActiveFilters"
    class="clear-btn btn white-bg fl-l"
    (click)="clearAllFilters()"
  >
    {{ "LABEL.CLEAR-ALL-FILTERS" | translate }}
  </div>

  <div class="fl-r">
    <app-elements-on-page
      [perPage]="perPage"
      (getItem)="perPage = $event; getItems(1)"
    ></app-elements-on-page>
  </div>
</div>

<div class="clear"></div>

<div class="ai-usage-totals">
  <div class="ai-usage-totals__item">
    <span class="ai-usage-totals__label">Total Cost</span>
    <span class="ai-usage-totals__value">
      {{ totals.costCents / 100 | currency : "USD" : "symbol" : "1.2-2" }}
    </span>
  </div>
  <div class="ai-usage-totals__item">
    <span class="ai-usage-totals__label">Total Tokens</span>
    <span class="ai-usage-totals__value">{{ totals.tokens | number }}</span>
  </div>
  <div class="ai-usage-totals__item">
    <span class="ai-usage-totals__label">Total Calls</span>
    <span class="ai-usage-totals__value">{{ totals.calls | number }}</span>
  </div>
</div>

<div class="table-wrapper">
  <div class="table-responsive">
    <table>
      <thead>
        <tr>
          <th>{{ "GENERALS.DATE" | translate }}</th>
          <th>Feature</th>
          <th>
            User
            <i
              class="icon-info-circled info-icon"
              tabindex="0"
              matTooltip="Who triggered the call. Shows N/A when there is no user to name — scheduled processing runs with nobody behind it, and calls recorded before per-user attribution shipped carry no user either."
            ></i>
          </th>
          <th>Model</th>
          <th class="text-right">Input</th>
          <th class="text-right">
            Cached
            <i
              class="icon-info-circled info-icon"
              tabindex="0"
              matTooltip="How many of the Input tokens were reused from the provider's cache instead of being processed again. This is part of Input, not extra."
            ></i>
          </th>
          <th class="text-right">
            Hit %
            <i
              class="icon-info-circled info-icon"
              tabindex="0"
              matTooltip="Share of the prompt that was reused from cache. Higher is cheaper. 0% means nothing was cached for this call."
            ></i>
          </th>
          <th class="text-right">Output</th>
          <th class="text-right">
            Cost ($)
            <i
              class="icon-info-circled info-icon"
              tabindex="0"
              matTooltip="What the client is charged. Cached tokens are billed at a reduced rate, so caching lowers this."
            ></i>
          </th>
          <th>{{ "GENERALS.STATUS" | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          *ngFor="
            let row of items
              | paginate
                : {
                    id: 'ai-usage',
                    itemsPerPage: itemsPage,
                    currentPage: p,
                    totalItems: total
                  }
          "
        >
          <td>{{ row.createdAt | date : "yyyy-MM-dd HH:mm" }}</td>
          <td>{{ row.feature }}</td>
          <!--
            "N/A" covers every case with no name to show: nobody triggered the
            call, the id no longer resolves to a user, or the row predates
            per-user attribution. Deliberately not the em dash used for
            unmeasured numbers — that glyph means "not reported", whereas this
            column is simply stating there is no user to name.
          -->
          <td>{{ row.userName || "N/A" }}</td>
          <td>{{ row.provider }}/{{ row.modelIdentifier }}</td>
          <td class="text-right">{{ row.inputTokens | number }}</td>
          <!--
            eqeqeq is disabled for the two cells below ONLY. Both use `!= null`
            on purpose: the loose form matches null AND undefined in one check,
            which is exactly the "not measured" discriminator described in the
            comments inside each cell. Rewriting either to `!== null` would let
            an undefined value fall through and render as blank/NaN instead of
            the notMeasured branch, so the rule is wrong here, not the code.
          -->
          <!-- eslint-disable @angular-eslint/template/eqeqeq -->
          <td class="text-right">
            <!--
              Discriminator is nullability, not truthiness: `!= null` lets a
              legitimate 0 (reported, nothing cached) through to render "0",
              while still routing a genuinely unreported row to the — branch.
              `*ngIf="row.cacheReadTokens"` would wrongly send 0 to notMeasured.
            -->
            <ng-container *ngIf="row.cacheReadTokens != null; else notMeasured">
              {{ row.cacheReadTokens | number }}
            </ng-container>
          </td>
          <!--
            Both guards are needed. The first distinguishes "not measured" (—)
            from "measured, nothing cached" (0%) using the same `!= null`
            nullability check as the Cached column above. The second catches a
            measured row with zero input tokens, where hitPercent() returns
            null rather than dividing by zero — without it that row would
            render NaN%.
          -->
          <td class="text-right">
            <ng-container
              *ngIf="
                row.cacheReadTokens != null && hitPercent(row) !== null;
                else notMeasured
              "
            >
              {{ hitPercent(row) }}%
            </ng-container>
          </td>
          <!-- eslint-enable @angular-eslint/template/eqeqeq -->
          <td class="text-right">{{ row.outputTokens | number }}</td>
          <td class="text-right">
            {{ row.costCents / 100 | currency : "USD" : "symbol" : "1.2-2" }}
          </td>
          <td>
            <span
              class="status-pill"
              [class.status-pill--ok]="row.success"
              [class.status-pill--err]="!row.success"
              [title]="row.errorMessage || ''"
            >
              {{ row.success ? "OK" : "Failed" }}
            </span>
          </td>
        </tr>
        <tr *ngIf="!isLoadingGeneral && (!items || items.length === 0)">
          <!--
            Two different messages because they call for two different actions.
            Filters combine, so feature X plus a user who never used X matches
            nothing legitimately — saying only "no results" there reads as "this
            account has no AI usage", which is the wrong conclusion entirely.
          -->
          <td colspan="10" class="empty-row">
            <ng-container *ngIf="hasActiveFilters; else noUsageAtAll">
              {{ "GENERALS.NO-RESULTS" | translate }} —
              <a class="clear-filters-link" (click)="clearAllFilters()">{{
                "LABEL.CLEAR-ALL-FILTERS" | translate
              }}</a>
            </ng-container>
            <ng-template #noUsageAtAll>
              {{ "GENERALS.NO-RESULTS" | translate }}
            </ng-template>
          </td>
        </tr>
      </tbody>
      <ng-template #notMeasured>&mdash;</ng-template>
    </table>
  </div>

  <div class="paginator">
    <pagination-controls
      id="ai-usage"
      (pageChange)="getItems($event)"
      previousLabel=""
      nextLabel=""
    ></pagination-controls>
  </div>
</div>
